import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";
import { STEP } from "#src/constants/step.ts";

import { useStepBeneficiaryPrimaryStore } from "#src/stores/step-beneficiary-primary.ts";
import { useStepEmployerStore } from "#src/stores/step-employer.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed } from "vue";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    revocable: required(REQS.OWNER_REVOCABLE),
    taxType: required(REQS.OWNER_TAX_TYPE),
    countryOfCitizenship: required(REQS.OWNER_COUNTRY_OF_CITIZENSHIP),
    birthLocation: required(REQS.OWNER_BIRTH_LOCATION),
    usCitizen: required(REQS.OWNER_US_CITIZEN),
    formationDate: required(REQS.OWNER_FORMATION_DATE),
    signerName: required(REQS.OWNER_SIGNER_NAME),
    signerEmail: required(REQS.OWNER_SIGNER_EMAIL),
    signerBirthdate: required(REQS.OWNER_SIGNER_BIRTHDATE),
  };
}

interface OwnerState extends BaseStepStoreState {
  requiredFields: Record<string, boolean>;
}

interface OwnerStep extends BaseStepStore, ToSetupState<OwnerState> {
  setStepRequirements(srq: DataRequirements): void;
}

function initialState(): OwnerState {
  return {
    inactive: false,
    requiredFields: initializeRequiredFields(),
  };
}

function initializeOwnerStep(pinia: Pinia): OwnerStep {
  const state = generateInitialState<OwnerState>(initialState());

  function $reset() {
    resetState<OwnerState>(state, initialState());
  }

  const step = computed(() => STEP.OWNER);
  const previous = computed(() => {
    const employer = useStepEmployerStore(pinia);
    if (employer.inactive) return employer.previous;
    return employer.step;
  });
  const next = computed(() => {
    const primary = useStepBeneficiaryPrimaryStore(pinia);
    if (primary.inactive) return primary.next;
    return primary.step;
  });

  function setStepRequirements(srq: DataRequirements) {
    state.inactive.value = !srq[REQS.OWNER];
    state.requiredFields.value = initializeRequiredFields(srq);
  }

  return {
    ...state,
    $reset,
    step,
    previous,
    next,
    setStepRequirements,
  };
}

export const useStepOwnerStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, OwnerStep>("step-owner", () => {
    return initializeOwnerStep(pinia);
  })(pinia, hot);
};
